import React, { useEffect, useState } from "react";
import { Menu, X, Globe } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useI18n } from "@/i18n/I18nProvider";

const Logo = () => (
  <a href="#top" className="flex items-center gap-2.5" data-testid="logo">
    <span
      className="grid h-8 w-8 place-items-center rounded-md hairline"
      style={{ background: "rgba(0,255,136,0.06)" }}
    >
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M5 7l7-3 7 3v6c0 4-3 7-7 8-4-1-7-4-7-8V7Z" stroke="#00FF88" strokeWidth="1.6"/>
        <path d="M9 12l2 2 4-4" stroke="#00FF88" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    </span>
    <span className="font-display font-bold tracking-tight text-ink text-[15px]">
      GuardMind
    </span>
  </a>
);

export default function Header() {
  const { t, locale, toggleLocale } = useI18n();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 6);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { href: "#how", label: t.nav.how },
    { href: "#features", label: t.nav.features },
    { href: "#pricing", label: t.nav.pricing },
    { href: "#faq", label: t.nav.faq },
  ];

  return (
    <header
      data-testid="site-header"
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
        scrolled
          ? "backdrop-blur-xl border-b"
          : "border-b border-transparent"
      }`}
      style={{
        background: scrolled ? "rgba(11,15,20,0.72)" : "transparent",
        borderColor: scrolled ? "rgba(245,247,250,0.08)" : "transparent",
      }}
    >
      <div className="container-px mx-auto flex max-w-7xl items-center justify-between py-3.5">
        <Logo />
        <nav className="hidden md:flex items-center gap-8" data-testid="desktop-nav">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              className="label-tag hover:text-primary transition-colors"
              data-testid={`nav-${l.href.replace("#","")}`}
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <button
            onClick={toggleLocale}
            className="hidden sm:inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 hairline mono-num text-[11px] text-ink/70 hover:text-ink hover:border-primary/30 transition-colors"
            aria-label="Change language"
            data-testid="locale-toggle"
          >
            <Globe size={12} />
            {locale === "pt-BR" ? "PT-BR" : "EN"}
          </button>
          <a href="#cta" className="hidden md:inline-flex btn-neon !py-2.5 !px-4 text-[12px]" data-testid="header-cta">
            {t.nav.cta}
          </a>
          <button
            className="md:hidden grid place-items-center h-9 w-9 rounded-md hairline"
            onClick={() => setOpen((o) => !o)}
            aria-label={open ? t.nav.close : t.nav.menu}
            data-testid="mobile-menu-button"
          >
            {open ? <X size={16} /> : <Menu size={16} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.22 }}
            className="md:hidden overflow-hidden border-t"
            style={{ borderColor: "rgba(245,247,250,0.08)", background: "rgba(11,15,20,0.95)" }}
            data-testid="mobile-nav"
          >
            <div className="container-px mx-auto flex flex-col py-4 gap-1">
              {links.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={() => setOpen(false)}
                  className="py-3 label-tag text-ink hover:text-primary"
                  data-testid={`mobile-nav-${l.href.replace("#","")}`}
                >
                  {l.label}
                </a>
              ))}
              <button
                onClick={() => { toggleLocale(); setOpen(false); }}
                className="mt-2 flex items-center gap-2 py-3 label-tag text-ink/70"
                data-testid="mobile-locale-toggle"
              >
                <Globe size={12} /> {locale === "pt-BR" ? "Switch to EN" : "Mudar para PT-BR"}
              </button>
              <a href="#cta" onClick={() => setOpen(false)} className="btn-neon mt-3" data-testid="mobile-cta">
                {t.nav.cta}
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
